// src/components/tasks/ui/BlockTaskDialogUI.tsx
import { cn } from "@/lib/utils"
import type { ReactNode, HTMLAttributes } from "react"

export const blockTaskDialogTheme = {
  // Большой прозрачный контейнер диалога
  dialogContent: "bg-transparent border-0 p-0 m-0 overflow-hidden max-w-7xl w-full h-[90vh] rounded-3xl",

  // Основной фон всего блока — стекло
  mainWrapper: "relative flex flex-col h-full bg-purple-950/80 backdrop-blur-2xl rounded-3xl overflow-hidden border border-purple-300/30",

  // Шапка — glass
  header: "relative z-30 flex items-center justify-between px-6 py-4 bg-purple-950/70 backdrop-blur-2xl border-b border-purple-300/40 text-white shadow-lg",

  // Левая панель (список подзадач)
  sidebar: "relative z-20 w-80 min-w-[140px] p-6 flex flex-col gap-6 overflow-y-auto border-r border-purple-300/30 bg-purple-950/40 backdrop-blur-xl",

  // Кнопка "Создать подзадачу"
  createSubtaskButton: "w-full py-6 text-lg font-medium bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg shadow-purple-500/30 flex items-center justify-center gap-3 rounded-3xl",

  // Карточка подзадачи — glass
  subtaskItem: (isSelected: boolean) => cn(
    "relative bg-purple-950/70 backdrop-blur-xl border border-purple-300/40 rounded-2xl p-4 text-sm text-purple-100 shadow-sm cursor-pointer transition-all duration-200",
    isSelected 
      ? "border-purple-400 bg-purple-900/80 shadow-md ring-2 ring-purple-400/50" 
      : "hover:bg-purple-950/80 hover:border-purple-300/60 hover:shadow-md"
  ),

  // Бейджи для подзадач (точно как в TaskCard)
  badgeNew: "absolute -top-2 -right-2 bg-emerald-500 text-white px-3 py-0.5 text-xs font-bold rounded-2xl shadow-lg z-10",
  badgeUnread: "absolute -top-2 -left-2 bg-orange-500 text-white rounded-2xl w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg animate-pulse z-10",

  // Правая часть (чат)
  chatContainer: "relative z-10 flex-1 overflow-hidden",

  // Пустое состояние в чате
  emptyChat: "h-full flex flex-col items-center justify-center text-purple-300 space-y-6 p-10 text-center",

  // Фон градиент по центру
  overlayGradient: "absolute inset-0 bg-gradient-to-b from-purple-800/70 to-purple-950/70 z-0",
} as const

// Styled-компоненты
export function BlockTaskHeader({ children }: { children: ReactNode }) {
  return <div className={blockTaskDialogTheme.header}>{children}</div>
}

export function SubtaskItem({
  isSelected,
  children,
  onClick,
  isNewTask = false,
  unreadMessages = 0,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  isSelected: boolean
  onClick?: () => void
  isNewTask?: boolean
  unreadMessages?: number
}) {
  return (
    <div
      className={cn(blockTaskDialogTheme.subtaskItem(isSelected), className)}
      onClick={onClick}
      {...props}
    >
      {/* Маркеры новых задач и сообщений */}
      {isNewTask && <div className={blockTaskDialogTheme.badgeNew}>Новая</div>}
      {unreadMessages > 0 && (
        <div className={blockTaskDialogTheme.badgeUnread}>
          {unreadMessages > 99 ? "99+" : unreadMessages}
        </div>
      )}

      {children}
    </div>
  )
}